import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalVariable } from '../../global';
import { environment } from '../../../environments/environment';
import { 
  ChangeRequestHistoryResponse, 
  ChangeRequestType,
  CreateChangeRequestDto,
  CreateChangeRequestRequest,
  CreateChangeRequestResponse,
  ChangeRequestListResponse
} from '../models/change-request.model';

// レスポンスDTO
interface ResponseDto<T> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class ChangeRequestService {
  // unregisteredAdminPathsリストをチェックして自動的に接続先を決定
  private apiUrl = this.getApiUrl();

  constructor(private http: HttpClient) {}

  private getApiUrl(): string {
    const path = '/admin/changerequest';
    const isUnregistered = environment.unregisteredAdminPaths?.includes(path);
    
    // ローカル開発環境（apiUrlにlocalhostが含まれる）の場合
    if (environment.apiUrl.includes('localhost')) {
      return `${environment.apiUrl}/ChangeRequest`;
    }
    
    if (environment.useApiGateway && !isUnregistered) {
      return `${environment.apiGatewayUrl}/changerequest`;  // APIM登録パスは小文字
    } else {
      return `${environment.directApiUrl}/ChangeRequest`;
    }
  }

  getChangeRequestHistory(jobOrderId: number): Observable<ChangeRequestHistoryResponse> {
    const params = new HttpParams().set('jobOrderId', jobOrderId.toString());
    return this.http.get<ResponseDto<ChangeRequestHistoryResponse>>(`${this.apiUrl}/history`, { params })
      .pipe(map(response => response.data));
  }

  getChangeRequestTypes(): Observable<ChangeRequestType[]> {
    return this.http.get<ResponseDto<ChangeRequestType[]>>(`${this.apiUrl}/types`)
      .pipe(map(response => response.data));
  }

  createChangeRequest(request: CreateChangeRequestDto): Observable<CreateChangeRequestResponse> {
    // キャメルケースからパスカルケースに変換
    const apiRequest: CreateChangeRequestRequest = {
      ChangeType: request.changeType,
      JobOrderId: request.jobOrderId,
      CutDate: request.cutDate,
      MngEmail: request.mngEmail,
      Comment: request.comment,
      ReasonText: request.reasonText
    };
    
    return this.http.post<ResponseDto<CreateChangeRequestResponse>>(`${this.apiUrl}`, apiRequest)
      .pipe(
        map(response => response.data)
      );
  }

  cancelChangeRequest(jobOrderId: number): Observable<any> {
    return this.http.delete<ResponseDto<boolean>>(`${this.apiUrl}/${jobOrderId}`)
      .pipe(map(response => response.data));
  }

  cancelChangeRequestById(requestId: number): Observable<any> {
    return this.http.delete<ResponseDto<boolean>>(`${this.apiUrl}/cancel/${requestId}`)
      .pipe(map(response => response.data));
  }

  // 変更依頼リスト取得（ページング対応）
  getAllChangeRequests(isCompleted: number, pageNo: number = 1, pageSize: number = 20, days: number = 0): Observable<ChangeRequestListResponse> {
    const params = new HttpParams()
      .set('isCompleted', isCompleted.toString())
      .set('pageNo', pageNo.toString())
      .set('pageSize', pageSize.toString())
      .set('days', days.toString());
    
    return this.http.get<ResponseDto<ChangeRequestListResponse>>(`${this.apiUrl}/all`, { params })
      .pipe(map(response => response.data));
  }

  // CSV出力用: ページングなしで全件取得
  getCompletedRequestsForExport(days: number): Observable<ChangeRequestListResponse> {
    const params = new HttpParams()
      .set('isCompleted', '1')
      .set('pageNo', '1')
      .set('pageSize', '999999')
      .set('days', days.toString());
    
    return this.http.get<ResponseDto<ChangeRequestListResponse>>(`${this.apiUrl}/all`, { params })
      .pipe(map(response => response.data));
  }

  // 変更依頼詳細取得
  getChangeRequestDetail(requestId: number): Observable<any> {
    return this.http.get<ResponseDto<any>>(`${this.apiUrl}/${requestId}`)
      .pipe(map(response => response.data));
  }

  // 処理状況保存
  saveProcessStatus(requestId: number, data: any): Observable<any> {
    return this.http.put<ResponseDto<any>>(`${this.apiUrl}/${requestId}/process`, data)
      .pipe(map(response => response.data));
  }

  // 別処理（変更なしで完了）
  alternativeProcess(requestId: number): Observable<any> {
    return this.http.put<ResponseDto<any>>(`${this.apiUrl}/${requestId}/alternative`, {})
      .pipe(map(response => response));
  }

  // 変更実行（削除/変更処理）
  executeChange(requestId: number): Observable<any> {
    return this.http.put<ResponseDto<any>>(`${this.apiUrl}/${requestId}/execute`, {})
      .pipe(map(response => response));
  }

  // 強制完了
  forceComplete(requestId: number): Observable<any> {
    return this.http.post<ResponseDto<boolean>>(`${this.apiUrl}/force-complete`, { requestId })
      .pipe(map(response => response));
  }
}
